import React, { useCallback, useState } from 'react';
import { ActivityIndicator, HelperText } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { useMutation } from 'react-query';
import { Platform } from 'react-native';
import { StackNavigationProp } from '@react-navigation/stack';
import { startThirdPartyAuth, useGetMyThirdPartyAuths } from '../../apis/appsyncApis';
import { screenNames } from '../../navigators/screenNames';
import { useI18n } from '../../context/I18nContext';
import ScrollablePage from '../../components/ui/ScrollablePage';
import { ListItemRow } from '../../components/ListItemRow';
import { ThirdPartyAuthProvider } from '../../types/appsync-types';
import { InformationStackRouteParams } from '../../navigators/InformationStackNavigator';
import Container from '../../components/Container';
import Typography from '../../components/Typography';

const thirdPartyAuthProviders = Object.values(ThirdPartyAuthProvider);

const AddThirdPartyAuth: React.FC = () => {
  const navigation = useNavigation<StackNavigationProp<InformationStackRouteParams, 'AddThirdPartyAuth'>>();
  const { I18n } = useI18n();
  const [isError, setIsError] = useState<boolean>(false);

  const getMyThirdPartyAuthsResult = useGetMyThirdPartyAuths();
  const myThirdPartyAuths = getMyThirdPartyAuthsResult.data?.data?.getMyThirdPartyAuths ?? [];

  const availableThirdPartyProviders = thirdPartyAuthProviders.filter(
    (provider) => !myThirdPartyAuths.some((auth) => auth?.authProvider === provider),
  );

  const startThirdPartyAuthMutation = useMutation(
    async (input: Parameters<typeof startThirdPartyAuth>[number]) => startThirdPartyAuth(input),
    {},
  );

  const thirdPartyAuthMutationAsync = useCallback(
    async (authProvider: ThirdPartyAuthProvider) => {
      setIsError(false);
      try {
        const result = await startThirdPartyAuthMutation.mutateAsync({
          input: { authProvider },
        });
        const authUrl = result.data?.startThirdPartyAuth?.authUrl;
        if (!authUrl) {
          setIsError(true);
          return;
        }
        if (Platform.OS === 'web') {
          window.location.href = authUrl;
        } else {
          navigation.navigate(screenNames.ThirdPartyAuthWebView, {
            url: authUrl,
            authProvider,
          });
        }
      } catch (error) {
        const errorString = JSON.stringify(error);
        console.log(errorString);
        setIsError(true);
      }
    },
    [navigation, startThirdPartyAuthMutation],
  );

  const renderAvailableAuthProviders = () => {
    return availableThirdPartyProviders.length ? (
      availableThirdPartyProviders.map((authProvider) => {
        return (
          <ListItemRow
            disabled={startThirdPartyAuthMutation.isLoading}
            key={authProvider}
            title={I18n.t(`thirdPartyAuth.provider.${authProvider ?? 'default'}`)}
            leftIcon={{ name: 'shield-account-outline', set: 'material-community' }}
            onPress={() => thirdPartyAuthMutationAsync(authProvider)}
            right={
              startThirdPartyAuthMutation.isLoading
                ? ({ color, style }) => <ActivityIndicator size={16} style={style} />
                : undefined
            }
          />
        );
      })
    ) : (
      <Typography variant="body1" style={{ paddingHorizontal: 16, paddingVertical: 8, textAlign: 'center' }}>
        {I18n.t('thirdPartyAuth.noThirdPartyAuthProviders')}
      </Typography>
    );
  };

  return (
    <ScrollablePage
      summary={{
        title: I18n.t('thirdPartyAuth.add.summaryTitle'),
        icon: 'lock',
      }}
    >
      {isError && (
        <Container>
          <HelperText type={'error'} visible>
            {I18n.t('thirdPartyAuth.startThirdPartyAuthFailed')}
          </HelperText>
        </Container>
      )}

      {getMyThirdPartyAuthsResult.isLoading ? <ActivityIndicator /> : renderAvailableAuthProviders()}
    </ScrollablePage>
  );
};

export default AddThirdPartyAuth;
